/**
 * This code is needed to enable the Preact Developer Tools.
 * It should be stripped out by Webpack in a production build as
 * it should automatically detect it as unreachable code.
 * Must be the first import.
 */
// Polyfill - see: https://github.com/babel/babel/issues/9849#issuecomment-487040428
import 'regenerator-runtime/runtime.js'

import Rails from 'rails-ujs'
import PreactRailsUJS from 'preact_ujs'

import { Application } from '@hotwired/stimulus'

// include desired GA autotrack plugins
import 'autotrack/lib/plugins/max-scroll-tracker'
import 'autotrack/lib/plugins/media-query-tracker'
import 'autotrack/lib/plugins/outbound-link-tracker'

// Lazy image loader
// import lazysizes-config before lazysizes
import '../vendor/lazysizes-config'
import 'lazysizes'
// Lazy load best image size for current viewport (deprecated plugin)
// https://github.com/aFarkas/lazysizes/tree/gh-pages/plugins/bgset
import 'lazysizes/plugins/bgset/ls.bgset'
// Calculates right sizes for `object-fit: cover` image
// https://github.com/aFarkas/lazysizes/tree/gh-pages/plugins/parent-fit
import 'lazysizes/plugins/parent-fit/ls.parent-fit'
// Polyfill responsive images (srcset and picture)
// https://github.com/aFarkas/lazysizes/tree/gh-pages/plugins/respimg
import 'lazysizes/plugins/respimg/ls.respimg'

import '../modules/expandable-content'
import '../modules/mapbox'
import '../modules/single-post'
import '../modules/story-stage'

import FrameFactory from '../components/frame-factory'

import AnalyticsController from '../controllers/analytics_controller'
import AnchorLinkController from '../controllers/anchor_link_controller'
import BundleEmbedController from '../controllers/bundle_embed_controller'
import EmbedModalController from '../controllers/embed_modal_controller'
import ExperienceLoaderController from '../controllers/experience_loader_controller'
import FrameController from '../controllers/frame_controller'
import IngredientAssistantController from '../controllers/ingredient_assistant_controller'
import IngredientListController from '../controllers/ingredient_list_controller'
import LanguageSwitcherController from '../controllers/language_switcher_controller'
import MapController from '../controllers/map_controller'
import ModalController from '../controllers/modal_controller'
import ModalTriggerController from '../controllers/modal_trigger_controller'
import MountJSController from '../controllers/mount_js_controller'
import PassportController from '../controllers/passport_controller'
import PoweredByProvenanceController from '../controllers/powered_by_provenance_controller'
import ProofPointStatusController from '../controllers/proof_point_status_controller'
import RecyclingController from '../controllers/recycling_controller'
import TimerEventsController from '../controllers/timer_events_controller'
import TipController from '../controllers/tip_controller'

import { initLogging } from '../utils/logger'

initLogging()

if (process.env.NODE_ENV === 'development') {
  // Must use require here as import statements are only allowed
  // to exist at top-level.
  require('preact/debug')
}

Rails.start()
PreactRailsUJS.useContext(require.context('react', true))
const application = Application.start()

const controllers = {
  analytics: AnalyticsController,
  'anchor-link': AnchorLinkController,
  'embed-modal': EmbedModalController,
  'bundle-embed': BundleEmbedController,
  frame: FrameController,
  'experience-loader': ExperienceLoaderController,
  'language-switcher': LanguageSwitcherController,
  'ingredient-assistant': IngredientAssistantController,
  'ingredient-list': IngredientListController,
  map: MapController,
  modal: ModalController,
  'modal-trigger': ModalTriggerController,
  passport: PassportController,
  'powered-by-provenance': PoweredByProvenanceController,
  'proof-point-status': ProofPointStatusController,
  'mount-js': MountJSController,
  recycling: RecyclingController,
  'timer-events': TimerEventsController,
  tip: TipController
}

Object.entries(controllers).forEach(([key, controller]) => {
  application.register(key, controller)
})

window.provenance = window.provenance || {}
window.provenance.frameFactory = new FrameFactory()

// This block handles trapping focus in a modal, to satisfy WCAG 2.1 SG 2.4.3
// See: https://www.w3.org/WAI/WCAG21/Understanding/focus-order.html
document.addEventListener('keydown', e => {
  if (e.key === 'Tab') {
    const lastElementInClaimOrAboutModal = document.querySelector('#submit-button, a.FooterCTA')

    if (document.activeElement === lastElementInClaimOrAboutModal) {
      document.querySelector('[data-modal-focus-target]').focus()
    }
  }
})
